import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import { Box } from "@mui/material";
import moment from "moment";

import { useLargeReportConfirm } from "src/hooks/useLargeReportConfirm";
import { useReportRangeLimit } from "src/hooks/useReportRangeLimit";

import { ActionButton, CardLayout, SelectField } from "../v3";
import { AdminService, ProjectService, PurchaseService } from "../../services";
import { getFilteredOptions, selectAllOption, selectAllUsers } from "../../utils";
import { vendorReportValidation } from "../../pages/report/validation";
import { ReportDateRangeFields } from "./ReportDateRangeFields.component";

export const VendorReportForm = ({ reportType = "summary", handleClear = () => {}, onGenerate = () => {} }) => {
  const { months: maxRangeMonths } = useReportRangeLimit();
  const { confirmLargeRange, largeRangeDialog } = useLargeReportConfirm();
  const [selectedVendors, setSelectedVendors] = useState([selectAllOption]);
  const [selectedProjects, setSelectedProjects] = useState([selectAllOption]);
  const [selectedUsers, setSelectedUsers] = useState([selectAllUsers]);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [reportDetails, setReportDetails] = useState({
    vendorIds: null,
    accountIds: null,
    projectIds: null,
    createdByIds: null,
    fromDate: moment().subtract(30, "days").format("YYYY-MM-DD"),
    toDate: moment().format("YYYY-MM-DD"),
  });

  const [searchParams] = useSearchParams();
  const vendorId = searchParams.get("vendorId");

  useEffect(() => {
    handleClear();
    setLoading(false);
  }, [reportType]);

  useEffect(() => {
    if (!vendorId) return;

    const fetchVendor = async () => {
      setLoading(true);
      try {
        const { data: vendor } = await PurchaseService.getVendorById(vendorId);
        setSelectedVendors([vendor]);
        setReportDetails((prev) => ({
          ...prev,
          vendorIds: [vendor.id],
          accountIds: vendor.accountId ? [vendor.accountId] : null,
        }));
        setErrors((prev) => ({ ...prev, vendorIds: "" }));
      } catch (error) {
        toast.error("Failed to fetch vendor details");
        console.error("🚀 ~ fetchVendor ~ error:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchVendor();
  }, [vendorId]);

  const updateDetailsState = (event) => {
    setLoading(false);
    handleClear();
    const { name, value } = event.target;
    setReportDetails((prev) => ({ ...prev, [name]: value }));
    if (name === "fromDate") setErrors((prev) => ({ ...prev, fromDate: "" }));
    else if (name === "toDate") setErrors((prev) => ({ ...prev, toDate: "" }));
  };

  const handleVendorChange = (vendors) => {
    setLoading(false);
    handleClear();
    const allSelected = vendors.some((vendor) => vendor.id === "all");

    if (allSelected) {
      setSelectedVendors([selectAllOption]);
      setReportDetails((prev) => ({ ...prev, vendorIds: null }));
    } else {
      // remove "all" if previously selected
      const filtered = vendors.filter((vendor) => vendor.id !== "all");
      setSelectedVendors(filtered);
      setReportDetails((prev) => ({
        ...prev,
        vendorIds: filtered.map((vendor) => vendor.id),
        accountIds: filtered.map((vendor) => vendor.accountId),
      }));
    }

    setErrors((prev) => ({ ...prev, vendorIds: "" }));
  };

  const handleProjectChange = (projects) => {
    setLoading(false);
    handleClear();
    const allSelected = projects.some((project) => project.id === "all");

    if (allSelected) {
      setSelectedProjects([selectAllOption]);
      setReportDetails((prev) => ({ ...prev, projectIds: null }));
    } else {
      // remove "all" if previously selected
      const filtered = projects.filter((project) => project.id !== "all");
      setSelectedProjects(filtered);
      setReportDetails((prev) => ({ ...prev, projectIds: filtered.map((project) => project.id) }));
    }

    setErrors((prev) => ({ ...prev, projectIds: "" }));
  };

  const handleUserChange = (users) => {
    setLoading(false);
    handleClear();
    const allSelected = users.some((user) => user.id === "all");

    if (allSelected) {
      setSelectedUsers([selectAllUsers]);
      setReportDetails((prev) => ({ ...prev, createdByIds: null }));
    } else {
      // remove "all" if previously selected
      const filtered = users.filter((user) => user.id !== "all");
      setSelectedUsers(filtered);
      setReportDetails((prev) => ({ ...prev, createdByIds: filtered.map((user) => user.id) }));
    }

    setErrors((prev) => ({ ...prev, createdByIds: "" }));
  };

  const handleGenerateReport = async () => {
    setErrors({});
    const validationErrors = vendorReportValidation(
      reportDetails,
      selectedVendors[0]?.id,
      selectedProjects[0]?.id,
      selectedUsers[0]?.id,
      maxRangeMonths,
    );
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);

      return;
    }

    if (!(await confirmLargeRange(reportDetails.fromDate, reportDetails.toDate))) return;
    try {
      setLoading(true);

      const isoFromDate = moment(reportDetails.fromDate).startOf("day").toISOString();
      const isoToDate = moment(reportDetails.toDate).endOf("day").toISOString();

      const payload = {
        ...reportDetails,
        fromDate: isoFromDate,
        toDate: isoToDate,
        reportType,
      };

      onGenerate?.(payload, {
        onError: () => setLoading(false),
      });
    } catch (error) {
      setLoading(false);
      toast.error("Failed to generate report");
      console.error("🚀 ~ handleGenerateReport ~ error:", error);
    }
  };

  return (
    <>
      <CardLayout>
        <Box mt={1}>
          <Box display="flex" gap={2}>
            <SelectField
              label="Vendor"
              value={selectedVendors}
              getData={PurchaseService.getVendors}
              getDataStaticFilters={{ type: "VENDOR", sortBy: "name", sortOrder: "asc" }}
              disabled={!!vendorId && selectedVendors[0]?.id !== selectAllOption.id}
              required
              multiSelect
              error={!!errors.vendorIds}
              helperText={errors.vendorIds}
              onChange={handleVendorChange}
              optionFilter={(options) => getFilteredOptions(options, selectedVendors, selectAllOption)}
              style={{ width: "100%" }}
            />
          </Box>
          <Box display="flex" gap={2}>
            <SelectField
              label="Project"
              value={selectedProjects}
              getData={ProjectService.getProjectList}
              getDataStaticFilters={{ sortBy: "name", sortOrder: "asc" }}
              required
              multiSelect
              error={!!errors.projectIds}
              helperText={errors.projectIds}
              onChange={handleProjectChange}
              optionFilter={(options) => getFilteredOptions(options, selectedProjects, selectAllOption)}
              style={{ width: "100%" }}
            />
            <SelectField
              label="Entry By"
              value={selectedUsers}
              getData={AdminService.getUserList}
              getDataStaticFilters={{ type: "USER", sortBy: "firstName", sortOrder: "asc" }}
              optionValueFields={["firstName", "lastName"]}
              required
              multiSelect
              error={!!errors.createdByIds}
              helperText={errors.createdByIds}
              onChange={handleUserChange}
              optionFilter={(options) => getFilteredOptions(options, selectedUsers, selectAllUsers)}
              style={{ width: "100%" }}
            />
          </Box>
          <ReportDateRangeFields
            fromDate={reportDetails.fromDate}
            toDate={reportDetails.toDate}
            onChange={updateDetailsState}
            errors={errors}
          />
          {largeRangeDialog}
        </Box>
        <Box display="flex" justifyContent="flex-end">
          <ActionButton buttonText="Generate" color="primary" disabled={loading} onButtonClick={handleGenerateReport} />
        </Box>
      </CardLayout>
    </>
  );
};
